import { ComponentPropsWithoutRef, FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormTextField from "../../../components/forms/FormTextField";
import { InitiateRecoverPasswordPayload, useAuthValidationSchemas } from "../../../datasources/requests/auth";

interface Props extends ComponentPropsWithoutRef<typeof Dialog> {
	onCancel: () => void;
	onConfirm: (request: InitiateRecoverPasswordPayload) => void;
}

/**
 * RecoverPassword renders a dialog that allows users to recover their account password.
 *
 * @param onCancel    the "cancel action" handler
 * @param onConfirm   the "confirm action" handler
 * @param props
 * @returns           a that allows users to recover their account password
 */
const RecoverPassword: FC<Props> = ({ onConfirm, onCancel, ...props }) => {
	const { t } = useTranslation("authentication");
	const { InitiateRecoverPasswordSchema } = useAuthValidationSchemas();

	const form = useForm<InitiateRecoverPasswordPayload>({
		defaultValues: { email: "" },
		resolver: zodResolver(InitiateRecoverPasswordSchema),
	});

	const handleInitiateRecoverAccount = form.handleSubmit((payload: InitiateRecoverPasswordPayload) => {
		onConfirm(payload);
		handleClose();
	});

	const handleClose = () => {
		form.reset({ email: "" });
		onCancel();
	};

	return (
		<Dialog open={props.open} onClose={handleClose}>
			<DialogTitle>{t("recover password", { context: "message" })}</DialogTitle>
			<DialogContent>
				<Box sx={{ pt: 1 }} />
				<FormTextField form={form} label={t("email")} name="email" fullWidth />
			</DialogContent>
			<DialogActions>
				<Button className="confirm" disabled={!form.formState.isValid} onClick={handleInitiateRecoverAccount}>
					{t("ok")}
				</Button>
				<Button className="cancel" onClick={handleClose}>
					{t("cancel")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RecoverPassword;
